import type { ColumnSizingState } from '@tanstack/react-table';
import { parseDataTableColumnWidth } from './data-table/columnWidth.js';

const MIN_COLUMN_WIDTH_PX = 56;
/** Declarative `act` column (row actions in-column) — not a flex grow column. */
export const DATA_TABLE_ACT_COLUMN_KEY = 'act';
export const DATA_TABLE_ACT_COLUMN_WIDTH_PX = 132;

function parsePercentWeight(width: string | undefined): number | null {
  if (!width?.trim()) return null;
  const raw = width.trim();
  if (!raw.endsWith('%')) return null;
  const pct = parseFloat(raw);
  return Number.isFinite(pct) && pct > 0 ? pct : null;
}

/**
 * Map declarative column widths (% weights) to pixel sizing that fills the scroll host.
 * Action column width is fixed first; data columns share the remainder by weight ratio.
 */
export function computeColumnSizingFromSpecs(
  columns: { key: string; width?: string }[],
  tableWidthPx: number,
  hasActionColumn: boolean,
  actionColumnWidthPx: number,
): ColumnSizingState {
  const hostW = Math.max(320, Math.round(tableWidthPx));
  const metaActionW = hasActionColumn ? actionColumnWidthPx : 0;

  const actColumns = columns.filter((c) => c.key === DATA_TABLE_ACT_COLUMN_KEY);
  const dataColumns = columns.filter((c) => c.key !== DATA_TABLE_ACT_COLUMN_KEY);

  const sizing: ColumnSizingState = {};
  let actReserved = 0;
  for (const col of actColumns) {
    const px = parseDataTableColumnWidth(
      col.width,
      hostW,
      DATA_TABLE_ACT_COLUMN_WIDTH_PX,
    );
    sizing[col.key] = px;
    actReserved += px;
  }

  const available = Math.max(
    MIN_COLUMN_WIDTH_PX * Math.max(dataColumns.length, 1),
    hostW - metaActionW - actReserved,
  );

  const weights = dataColumns.map((c) => parsePercentWeight(c.width) ?? 1);
  const weightSum = weights.reduce((a, w) => a + w, 0) || 1;

  let used = 0;
  for (let i = 0; i < dataColumns.length; i += 1) {
    const col = dataColumns[i]!;
    const w = weights[i] ?? 1;
    const isLastData = i === dataColumns.length - 1;
    const px = isLastData
      ? Math.max(MIN_COLUMN_WIDTH_PX, available - used)
      : Math.max(MIN_COLUMN_WIDTH_PX, Math.round((available * w) / weightSum));
    sizing[col.key] = px;
    used += px;
  }

  return sizing;
}

export function colgroupPercentWidth(widthPx: number, totalPx: number): string {
  if (totalPx <= 0) return 'auto';
  const pct = (widthPx / totalPx) * 100;
  return `${pct.toFixed(4)}%`;
}
